import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

interface MessageInputProps {
  onSend: (message: string) => boolean
  disabled?: boolean
}

export default function MessageInput({ onSend, disabled }: MessageInputProps) {
  const MAX_LENGTH = 10000
  const [message, setMessage] = useState('')
  const [isExpanded, setIsExpanded] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { t } = useTranslation()

  // Auto-resize textarea and track if multiline
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      const scrollHeight = textareaRef.current.scrollHeight
      textareaRef.current.style.height = scrollHeight + 'px'
      setIsExpanded(scrollHeight > 56)
    }
  }, [message])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (message.trim() && !disabled) {
      const sent = onSend(message)
      if (sent) {
        setMessage('')
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto'
        }
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <div className="p-4 bg-white">
      <div className="max-w-4xl mx-auto flex items-center gap-2 border border-gray-200 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all focus-within:ring-2 focus-within:ring-brand-100 focus-within:border-brand group">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_LENGTH))}
          onKeyDown={handleKeyDown}
          placeholder={t('input.placeholder')}
          disabled={disabled}
          maxLength={MAX_LENGTH}
          rows={1}
          className={`flex-1 pl-4 py-4 bg-transparent border-none resize-none outline-none focus:ring-0 focus:border-none text-sm text-gray-900 placeholder-gray-500 max-h-48 ${
            isExpanded ? 'overflow-y-auto' : 'overflow-hidden'
          }`}
        />
        <button
          onClick={handleSubmit}
          disabled={!message.trim() || disabled}
          className="self-center mr-3 p-1.5 bg-brand text-white rounded-lg hover:bg-brand-hover transition duration-150 shadow-sm opacity-0 group-focus-within:opacity-100 disabled:opacity-0"
          title={t('input.sendMessage')}
        >
          <svg className="w-5 h-5 transform rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      </div>
      <div className="text-center mt-2 flex flex-col items-center">
        {message.length >= MAX_LENGTH * 0.8 && (
          <p className={`text-xs mb-1 ${message.length >= MAX_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
            {message.length}/{MAX_LENGTH} characters
          </p>
        )}
        <p className="text-sm text-gray-400">
          {t('input.footerDisclaimer')}
        </p>
      </div>
    </div>
  )
}
